/**
 * The four approved facts an article is written from.
 *
 * Two screens choose them - the plan screen before generation and the editor
 * after it - and both must offer exactly the set articles/context.ts will read,
 * or the writer picks something the generator then reports as missing. One
 * definition, so that cannot drift.
 */
import type { CatalogItem, ListResponse } from '../api.ts';
import { Field } from './EditPanel.tsx';
import { Select } from './Select.tsx';

export const MATERIAL = [
  {
    key: 'personaId',
    list: 'personas',
    label: 'ペルソナ',
    name: 'label',
    hint: '構成案・本文の生成に必要です。',
  },
  {
    key: 'treatmentId',
    list: 'treatments',
    label: '施術',
    name: 'name',
    hint: '本文の生成に必要です。価格・リスク・機器はこの施術の登録値だけを使います。',
  },
  { key: 'supervisorId', list: 'supervisors', label: '監修医', name: 'name', hint: '' },
  { key: 'ctaTemplateId', list: 'ctaTemplates', label: 'CTA', name: 'name', hint: '' },
] as const;

export type MaterialKey = (typeof MATERIAL)[number]['key'];
export type MaterialSelection = Record<MaterialKey, string>;

export interface MaterialOptions {
  personas: ListResponse<CatalogItem>;
  treatments: ListResponse<CatalogItem>;
  supervisors: ListResponse<CatalogItem>;
  ctaTemplates: ListResponse<CatalogItem>;
}

/** Catalog rows carry open-ended columns; only strings are safe to render. */
const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The article's stored references, as the form holds them ('' is "none"). */
export const materialFrom = (article: {
  persona_id: string | null;
  treatment_id: string | null;
  supervisor_id: string | null;
  cta_template_id: string | null;
}): MaterialSelection => ({
  personaId: article.persona_id ?? '',
  treatmentId: article.treatment_id ?? '',
  supervisorId: article.supervisor_id ?? '',
  ctaTemplateId: article.cta_template_id ?? '',
});

/** The form as PATCH fields: '' clears the reference, an id sets it. */
export const materialPayload = (selection: MaterialSelection): Record<string, string | null> =>
  Object.fromEntries(
    MATERIAL.map(({ key }) => [key, selection[key] === '' ? null : selection[key]]),
  );

export function MaterialPicker({
  value,
  onChange,
  options,
  idPrefix,
}: {
  value: MaterialSelection;
  onChange: (next: MaterialSelection) => void;
  options: MaterialOptions;
  /** Two of these can exist on one page; ids have to differ. */
  idPrefix: string;
}) {
  return (
    <>
      {MATERIAL.map(({ key, list, label, name, hint }) => {
        const items = options[list].items;
        const id = `${idPrefix}-${key}`;
        return (
          <Field
            key={key}
            label={label}
            htmlFor={id}
            {...(items.length === 0
              ? { hint: `承認済みの${label}がありません。${hint}` }
              : hint
                ? { hint }
                : {})}
          >
            <Select
              id={id}
              value={value[key]}
              disabled={items.length === 0}
              onChange={(next) => onChange({ ...value, [key]: next })}
              options={[
                { value: '', label: '未選択' },
                ...items.map((item) => ({
                  value: item.id,
                  label: asText(item[name]) || '（名称なし）',
                })),
              ]}
            />
          </Field>
        );
      })}
    </>
  );
}
